import { useCallback, useRef } from "react";
import { BsClock } from "react-icons/bs";
import {
    color_black,
    color_grey_dark,
    color_red_default,
    color_white,
} from "../../../../constants/colors";
import {
    BILLING_OPTIONS,
    formatScheduleSlot,
    isScheduleSlotEndBeforeStart,
    parseScheduleSlot,
} from "../../../../utilities/scheduleSlotUtils";
import { useWindowDimensions } from "../../../../utils";
import ArrowIconDown from "../../../icons/ArrowIconDown";
import {
    OnestBoldSmall,
    OnestNormalDefault,
    OnestNormalSmall,
} from "../../../styled/TextComponents";
import { styles } from "./styles";
import { useI18n } from "../../../../i18n/I18nProvider";

function getBillingOptions(quantity) {
  if (quantity <= 2) {
    return BILLING_OPTIONS;
  }
  return [...BILLING_OPTIONS, { value: quantity, label: String(quantity) }];
}

const billingColumnWidth = 48;
const dayColumnWidth = 108;
const desktopTimeRowColumns = `${dayColumnWidth}px minmax(0, 1fr) ${billingColumnWidth}px`;
const timeFieldIconReserve = 30;

const weekDayNames = {
  ПН: "Понедельник",
  ВТ: "Вторник",
  СР: "Среда",
  ЧТ: "Четверг",
  ПТ: "Пятница",
  СБ: "Суббота",
  ВС: "Воскресенье",
};

const SelectWithArrow = ({ children, style }) => (
  <div style={{ position: "relative", ...style }}>
    {children}
    <div
      style={{
        position: "absolute",
        right: "10px",
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
      }}
    >
      <ArrowIconDown fill="#B1B1B1" width={10} height={7} />
    </div>
  </div>
);

const ScheduleTimeField = ({ value, onChange, ariaLabel, hasConflict }) => {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const isEmpty = !value;

  const openPicker = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const input = inputRef.current;
    if (!input) {
      return;
    }
    // Toggle behavior: second click closes the native picker.
    if (document.activeElement === input) {
      input.blur();
      return;
    }
    try {
      input.focus();
      if (typeof input.showPicker === "function") {
        input.showPicker();
      } else {
        input.click?.();
      }
    } catch {
      input.click?.();
    }
  };

  return (
    <div
      style={{
        ...styles.timeFieldWrapper,
        ...(hasConflict
          ? {
              boxShadow: `inset 0 0 0 1px ${color_red_default}`,
              borderRadius: 10,
            }
          : {}),
      }}
    >
      <input
        ref={inputRef}
        type="time"
        className="student-schedule-time-input"
        value={value}
        onChange={onChange}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: "absolute",
          opacity: 0,
          width: 1,
          height: 1,
          padding: 0,
          margin: 0,
          border: "none",
          pointerEvents: "none",
        }}
      />
      <button
        type="button"
        aria-label={ariaLabel}
        onMouseDown={openPicker}
        style={{
          ...styles.timeFieldInput,
          position: "relative",
          width: "100%",
          paddingLeft: 8,
          paddingRight: timeFieldIconReserve,
          cursor: "pointer",
          display: "block",
          textAlign: "left",
          fontFamily: "Onest",
          fontSize: isEmpty ? 13 : 16,
          lineHeight: 1,
          boxSizing: "border-box",
        }}
      >
        <span
          style={{
            display: "block",
            minWidth: 0,
            textAlign: "left",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            userSelect: "none",
            fontVariantNumeric: "tabular-nums",
            fontFeatureSettings: '"tnum"',
            fontSize: isEmpty ? 13 : 16,
            color: isEmpty ? color_grey_dark : color_black,
            fontWeight: isEmpty ? 400 : 500,
            letterSpacing: isEmpty ? 0 : "0.01em",
          }}
        >
          {value || t("чч:мм")}
        </span>
        <BsClock
          size={14}
          color={color_grey_dark}
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
          }}
        />
      </button>
    </div>
  );
};

const ScheduleTimeRangeRow = ({
  weekday,
  start,
  end,
  onStartChange,
  onEndChange,
  hasConflict,
}) => {
  const { t } = useI18n();
  return (
  <div style={styles.timeRangeGroup}>
    <ScheduleTimeField
      value={start}
      onChange={onStartChange}
      ariaLabel={t("Начало, {{weekday}}", { weekday: t(weekDayNames[weekday]) })}
      hasConflict={hasConflict}
    />
    <OnestNormalSmall style={styles.timeRangeArrow}>→</OnestNormalSmall>
    <ScheduleTimeField
      value={end}
      onChange={onEndChange}
      ariaLabel={t("Конец, {{weekday}}", { weekday: t(weekDayNames[weekday]) })}
      hasConflict={hasConflict}
    />
  </div>
  );
};

export const WeekdayTimeSelector = ({
  selectedWeekdays,
  selectedTimes,
  onTimeChange,
  conflictsByWeekday = {},
  formatConflictMessage,
}) => {
  const { t } = useI18n();
  const { width: screenWidth } = useWindowDimensions();
  const isCompact = screenWidth <= 540;

  const handleSlotChange = useCallback(
    (weekday, start, end, quantity) => {
      onTimeChange(weekday, formatScheduleSlot(start, end, quantity));
    },
    [onTimeChange],
  );

  if (selectedWeekdays.length === 0) {
    return (
      <div style={{ marginTop: 8 }}>
        <div
          style={{
            height: 40,
            width: "100%",
            borderRadius: 10,
            backgroundColor: color_white,
            color: color_grey_dark,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {screenWidth >= 768 ? (
            <OnestNormalDefault>{t("Выберите дни недели выше")}</OnestNormalDefault>
          ) : (
            <OnestNormalSmall>{t("Выберите дни недели выше")}</OnestNormalSmall>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.timeSelectContainer, gap: 10, marginTop: 8 }}>
      {!isCompact && (
        <div style={styles.timeTableHeaderRow}>
          <span style={styles.timeTableHeaderCell}>{t("День")}</span>
          <div style={styles.timeTableHeaderTimeRange}>
            <span style={styles.timeTableHeaderCell}>{t("С")}</span>
            <span />
            <span style={styles.timeTableHeaderCell}>{t("До")}</span>
          </div>
          <span />
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {selectedWeekdays.map((weekday) => {
          const slotValue = selectedTimes[weekday];
          const { start, end, quantity } = slotValue
            ? parseScheduleSlot(slotValue)
            : { start: "", end: "", quantity: 1 };
          const isEndBeforeStart = isScheduleSlotEndBeforeStart(
            selectedTimes[weekday],
          );
          const weekdayConflicts = conflictsByWeekday[weekday] || [];
          const conflictMessage = formatConflictMessage
            ? formatConflictMessage(weekdayConflicts)
            : null;

          return (
            <div key={weekday}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isCompact
                    ? "1fr"
                    : desktopTimeRowColumns,
                  gap: 8,
                  alignItems: "center",
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                  boxSizing: "border-box",
                }}
              >
                <OnestBoldSmall
                  style={{
                    color: color_black,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}
                >
                  {t(weekDayNames[weekday])}
                </OnestBoldSmall>

                {isCompact ? (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      display: "grid",
                      gridTemplateColumns: `1fr ${billingColumnWidth}px`,
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <ScheduleTimeRangeRow
                      weekday={weekday}
                      start={start}
                      end={end}
                      hasConflict={weekdayConflicts.length > 0}
                      onStartChange={(e) =>
                        handleSlotChange(weekday, e.target.value, end, quantity)
                      }
                      onEndChange={(e) =>
                        handleSlotChange(
                          weekday,
                          start,
                          e.target.value,
                          quantity,
                        )
                      }
                    />
                    <SelectWithArrow
                      style={{ width: billingColumnWidth, justifySelf: "end" }}
                    >
                      <select
                        aria-label={t("Списание с баланса: 1 или 2, {{weekday}}", { weekday: t(weekDayNames[weekday]) })}
                        title={t("Сколько уроков списать с баланса (см. подсказку ⓘ)")}
                        value={quantity}
                        onChange={(e) =>
                          handleSlotChange(
                            weekday,
                            start,
                            end,
                            parseInt(e.target.value, 10),
                          )
                        }
                        style={{
                          ...styles.timeFieldInput,
                          width: billingColumnWidth,
                          paddingLeft: 0,
                          paddingRight: 22,
                          textAlign: "center",
                        }}
                      >
                        {getBillingOptions(quantity).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </SelectWithArrow>
                  </div>
                ) : (
                  <ScheduleTimeRangeRow
                    weekday={weekday}
                    start={start}
                    end={end}
                    hasConflict={weekdayConflicts.length > 0}
                    onStartChange={(e) =>
                      handleSlotChange(weekday, e.target.value, end, quantity)
                    }
                    onEndChange={(e) =>
                      handleSlotChange(weekday, start, e.target.value, quantity)
                    }
                  />
                )}

                {!isCompact && (
                  <SelectWithArrow
                    style={{ width: billingColumnWidth, justifySelf: "end" }}
                  >
                    <select
                      aria-label={t("Списание с баланса: 1 или 2, {{weekday}}", { weekday: t(weekDayNames[weekday]) })}
                      title={t("Сколько уроков списать с баланса (см. подсказку ⓘ)")}
                      value={quantity}
                      onChange={(e) =>
                        handleSlotChange(
                          weekday,
                          start,
                          end,
                          parseInt(e.target.value, 10),
                        )
                      }
                      style={{
                        ...styles.timeFieldInput,
                        width: billingColumnWidth,
                        paddingLeft: 0,
                        paddingRight: 22,
                        textAlign: "center",
                      }}
                    >
                      {getBillingOptions(quantity).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </SelectWithArrow>
                )}
              </div>
              {isEndBeforeStart && (
                <OnestNormalSmall
                  style={{ color: color_red_default, marginTop: 4 }}
                >
                  {t("Время окончания должно быть позже начала")}
                </OnestNormalSmall>
              )}
              {!isEndBeforeStart && conflictMessage && (
                <OnestNormalSmall
                  style={{
                    color: color_red_default,
                    marginTop: 4,
                    lineHeight: "140%",
                  }}
                >
                  {conflictMessage}
                </OnestNormalSmall>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
